import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { catalogueRepository } from "@/repositories/catalogueRepository";

/**
 * Query keys, in one place — same reasoning as `useSettingsData`: an invalidation
 * key typed by hand in two files eventually diverges by one character and the
 * list silently stops refreshing after a write.
 */
export const catalogueKeys = {
  products: (q: string, category: string) =>
    ["catalogue", "products", q, category] as const,
  categories: ["catalogue", "categories"] as const,
  master: (q: string, category: string) =>
    ["catalogue", "master", q, category] as const,
  adopted: ["catalogue", "adopted"] as const,
  imports: ["catalogue", "imports"] as const,
  importJob: (id: string) => ["catalogue", "imports", id] as const,
  importLimits: ["catalogue", "imports", "limits"] as const,
};

export const useProducts = (q: string, category: string) =>
  useQuery({
    queryKey: catalogueKeys.products(q, category),
    queryFn: () =>
      catalogueRepository.products({ q, category: category || undefined }),
    // The catalogue does not change under the user's feet while they read it,
    // and a search-as-you-type screen refetching on every window focus is
    // visible flicker for no information.
    staleTime: 30_000,
  });

export const useCategories = () =>
  useQuery({
    queryKey: catalogueKeys.categories,
    queryFn: catalogueRepository.categories,
  });

export const useMasterSearch = (
  q: string,
  category: string,
  enabled: boolean,
) =>
  useQuery({
    queryKey: catalogueKeys.master(q, category),
    queryFn: () => catalogueRepository.searchMaster(q, category || undefined),
    enabled,
    staleTime: 5 * 60_000, // Platform data. It changes on deploys, not on use.
  });

/** What this shop has already taken from the shared list. */
export const useAdoptedMasters = () =>
  useQuery({
    queryKey: catalogueKeys.adopted,
    queryFn: catalogueRepository.adoptedMasters,
    staleTime: 30_000,
  });

export const useImportLimits = () =>
  useQuery({
    queryKey: catalogueKeys.importLimits,
    queryFn: catalogueRepository.importLimits,
    staleTime: Infinity,
  });

export const useImports = () =>
  useQuery({
    queryKey: catalogueKeys.imports,
    queryFn: catalogueRepository.imports,
  });

/** Refresh the catalogue after anything that adds to it. */
export function useRefreshCatalogue() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: ["catalogue", "products"] });
    void client.invalidateQueries({ queryKey: catalogueKeys.categories });
    // The shared list marks what a shop already has, so adding to the shop
    // changes what the shared list should show.
    void client.invalidateQueries({ queryKey: catalogueKeys.adopted });
  };
}

export function useCopyFromMaster() {
  const refresh = useRefreshCatalogue();
  return useMutation({
    mutationFn: catalogueRepository.copyFromMaster,
    onSuccess: refresh,
  });
}
